import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { durations } from '../../theme';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface CrossfadeProps {
  /** True while the data is on its way. Only the skeleton renders. */
  loading: boolean;
  skeleton: React.ReactNode;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

/**
 * Shows `skeleton` while `loading`, then fades the real content in over
 * `durations.base` while the skeleton fades out on top of it. Children mount
 * only once loading ends, so nothing of the loaded state (an empty-state
 * line, say) exists behind the skeleton. Under reduced motion the swap is
 * instant. The skeleton unmounts when the fade finishes, never mid-fade
 * (animated-fade-freeze lesson).
 */
export function Crossfade({ loading, skeleton, children, style }: CrossfadeProps) {
  const reducedMotion = useReducedMotion();
  const progress = useRef(new Animated.Value(loading ? 0 : 1)).current;
  const [overlay, setOverlay] = useState(loading);
  const wasLoading = useRef(loading);

  useEffect(() => {
    if (loading) {
      wasLoading.current = true;
      progress.setValue(0);
      setOverlay(true);
      return;
    }
    // Also re-enter while the overlay is still up, so a reduced-motion flip
    // mid-fade finishes the swap instead of stranding the skeleton.
    if (!wasLoading.current && !overlay) return;
    wasLoading.current = false;
    if (reducedMotion) {
      progress.setValue(1);
      setOverlay(false);
      return;
    }
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: durations.base,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => {
      if (finished) setOverlay(false);
    });
    return () => animation.stop();
  }, [loading, reducedMotion, progress, overlay]);

  const skeletonOpacity = progress.interpolate({ inputRange: [0, 1], outputRange: [1, 0] });

  if (loading) return <View style={style}>{skeleton}</View>;

  return (
    <View style={[overlay && styles.clip, style]}>
      <Animated.View style={{ opacity: progress }}>{children}</Animated.View>
      {overlay ? (
        <Animated.View pointerEvents="none" style={[styles.overlay, { opacity: skeletonOpacity }]}>
          {skeleton}
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  clip: { overflow: 'hidden' },
  overlay: { position: 'absolute', top: 0, left: 0, right: 0 },
});
